import { useEffect, useState } from "react";
import { Shell } from "../../components/Shell";
import { Button, Empty, ErrorNotice, Field, Input, Loading, Modal, PageHead, Pager, Panel, SecretReveal, Segmented, StatusBadge, useToast } from "../../components/ui";
import { adminApi } from "../../lib/api";
import { invalidate, useAction, useQuery } from "../../lib/query";
import { Link, navigate, useSearchParam } from "../../lib/router";
import { useClient } from "../../lib/session";
import { formatDateTime, relativeTime } from "../../lib/time";
import type { Credential, Developer } from "../../lib/types";

export function AdminDevelopers() {
  const api = adminApi(useClient());
  const [q, setQ] = useSearchParam("q");
  const [status, setStatus] = useSearchParam("status");
  const [newParam, setNewParam] = useSearchParam("new");
  const [offset, setOffset] = useState(0);
  const [search, setSearch] = useState(q);
  const limit = 25;

  useEffect(() => {
    const t = window.setTimeout(() => {
      if (search !== q) {
        setOffset(0);
        setQ(search);
      }
    }, 300);
    return () => window.clearTimeout(t);
  }, [search]);

  const list = useQuery(`admin:developers:${q}:${status}:${offset}`, () => api.developers({ q, status, limit, offset }));

  return (
    <Shell crumbs={[{ label: "Developers" }]}>
      <PageHead
        title="Developers"
        lede="Studios with access to the engine. Registering one gives them a developer credential to create games and keys."
        actions={
          <Button variant="primary" onClick={() => setNewParam("1")}>
            Register developer
          </Button>
        }
      />
      <Panel
        flush
        title={list.data ? `${list.data.total} developer${list.data.total === 1 ? "" : "s"}` : "Developers"}
        actions={
          <div className="row">
            <Input
              type="search"
              placeholder="Search name, email or ID"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              aria-label="Search developers"
              style={{ width: 240 }}
            />
            <Segmented
              label="Filter by status"
              value={status || "all"}
              onChange={(v) => {
                setOffset(0);
                setStatus(v === "all" ? "" : v);
              }}
              options={[
                { value: "all", label: "All" },
                { value: "active", label: "Active" },
                { value: "suspended", label: "Suspended" },
              ]}
            />
          </div>
        }
      >
        <ErrorNotice error={list.error} onRetry={list.reload} />
        {list.loading && !list.data && <Loading />}
        {list.data && list.data.data.length === 0 && (
          <Empty title={q || status ? "No developers match" : "No developers yet"} action={!q && !status ? <Button variant="primary" onClick={() => setNewParam("1")}>Register the first developer</Button> : undefined}>
            {q || status ? "Try a different search or filter." : "Register a studio to give it access to the engine."}
          </Empty>
        )}
        {list.data && list.data.data.length > 0 && (
          <>
            <div className="table-wrap">
              <table className="data">
                <thead>
                  <tr>
                    <th>Developer</th>
                    <th>Status</th>
                    <th className="num">Games</th>
                    <th className="num">Active keys</th>
                    <th>Last active</th>
                    <th>Joined</th>
                  </tr>
                </thead>
                <tbody>
                  {list.data.data.map((d) => (
                    <tr key={d.id} className="clickable" onClick={() => navigate(`/admin/developers/${d.id}`)}>
                      <td>
                        <Link to={`/admin/developers/${d.id}`} className="cell-title" onClick={(e) => e.stopPropagation()}>
                          {d.name}
                        </Link>
                        <div className="cell-sub">{d.contact_email}</div>
                      </td>
                      <td><StatusBadge status={d.status} /></td>
                      <td className="num">{d.games}</td>
                      <td className="num">{d.active_api_keys}</td>
                      <td>{relativeTime(d.last_active_at)}</td>
                      <td>{formatDateTime(d.created_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <Pager total={list.data.total} limit={limit} offset={offset} onChange={setOffset} />
          </>
        )}
      </Panel>
      <RegisterDeveloper open={newParam === "1"} onClose={() => setNewParam("")} />
    </Shell>
  );
}

function RegisterDeveloper({ open, onClose }: { open: boolean; onClose: () => void }) {
  const api = adminApi(useClient());
  const toast = useToast();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [created, setCreated] = useState<{ developer: Developer; credential: Credential } | null>(null);
  const create = useAction(() => api.createDeveloper({ name: name.trim(), contact_email: email.trim() }));

  const close = () => {
    setName("");
    setEmail("");
    setCreated(null);
    create.setError(null);
    onClose();
  };

  const submit = async () => {
    const r = await create.run();
    if (r) {
      setCreated(r);
      invalidate("admin:");
      toast(`Registered ${r.developer.name}`);
    }
  };

  return (
    <Modal
      open={open}
      onClose={close}
      title={created ? `${created.developer.name} is registered` : "Register a developer"}
      description={created ? undefined : "They'll receive a developer credential to sign in here and to call the management API."}
      onSubmit={created ? close : submit}
      footer={
        created ? (
          <>
            <Button onClick={() => navigate(`/admin/developers/${created.developer.id}`)}>Open developer</Button>
            <Button type="submit" variant="primary">Done</Button>
          </>
        ) : (
          <>
            <Button onClick={close}>Cancel</Button>
            <Button type="submit" variant="primary" busy={create.pending}>Register developer</Button>
          </>
        )
      }
    >
      {created ? (
        <div className="stack" style={{ gap: 14 }}>
          <SecretReveal secret={created.credential.secret ?? ""} what="credential" />
          <p className="small muted">
            Send it to {created.developer.contact_email} through a password manager or another secure channel, not plain email.
            If it's lost, issue a new one from the developer's page and revoke this one.
          </p>
        </div>
      ) : (
        <div className="form">
          <Field label="Studio name">
            <Input value={name} onChange={(e) => setName(e.target.value)} required maxLength={120} autoFocus placeholder="Pixel Forge" />
          </Field>
          <Field label="Contact email">
            <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required placeholder="ops@pixelforge.com" />
          </Field>
          <ErrorNotice error={create.error} />
        </div>
      )}
    </Modal>
  );
}
